import { deepEqual } from "../utils";

/**
 * Transfer data structure.
 *
 * Generic parameter `T` allows strict-typing when the transfer shape is known
 * (e.g. via codegen-generated interfaces). Falls back to an open index signature
 * when omitted so existing untyped code keeps compiling.
 *
 */
export interface TransferData {
	__identifier?: string;
	__signedIdentifier?: string;
	__typename?: string;
	/** Index signature preserved for backwards-compatibility when T = Record<string, unknown> */
	[key: string]: unknown;
}

/**
 * Strict transfer data — a TransferData whose *own* properties conform to `T`.
 *
 * Use this alias in generated code and userland APIs where the exact field set is
 * known at compile time.  The intersection keeps the internal `__*` fields from
 * `TransferData` while adding compile-time keys from `T`.
 *
 * @typeParam T - Strict field definitions (e.g. `{ name: string; age: number }`)
 */
export type StrictTransferData<T extends Record<string, unknown> = Record<string, unknown>> = TransferData & T;

/**
 * Data store state per transfer type.
 */
export interface TransferState {
	data: TransferData | null;
	originalData: TransferData | null;
	isDirty: boolean;
	isLoading: boolean;
	error: Error | null;
}

/**
 * Full store state.
 */
export interface DataStoreState {
	transfers: Map<string, TransferState>;
	version: number;
}

/**
 * Selector for fine-grained subscriptions.
 */
export type DataSelector<T> = (state: DataStoreState) => T;

/**
 * Create empty transfer state.
 */
export function createEmptyTransferState(): TransferState {
	return {
		data: null,
		originalData: null,
		isDirty: false,
		isLoading: false,
		error: null,
	};
}

/**
 * Data store with selector-based subscriptions.
 * Provides fine-grained reactivity by only notifying subscribers
 * when their selected value actually changes.
 */
export class DataStore {
	private state: DataStoreState = {
		transfers: new Map(),
		version: 0,
	};

	private subscribers = new Map<DataSelector<unknown>, Set<() => void>>();
	private selectorCache = new Map<DataSelector<unknown>, unknown>();

	/**
	 * Subscribe to specific data slice.
	 * Only notifies when selected value changes.
	 *
	 * @param selector - Function to select data from state
	 * @param callback - Callback to invoke when selected data changes
	 * @returns Unsubscribe function
	 */
	subscribe<T>(selector: DataSelector<T>, callback: () => void): () => void {
		if (!this.subscribers.has(selector)) {
			this.subscribers.set(selector, new Set());
			// Initialize cache with current value
			this.selectorCache.set(selector, selector(this.state));
		}
		this.subscribers.get(selector)!.add(callback);

		return () => {
			const subs = this.subscribers.get(selector);
			if (subs) {
				subs.delete(callback);
				if (subs.size === 0) {
					this.subscribers.delete(selector);
					this.selectorCache.delete(selector);
				}
			}
		};
	}

	/**
	 * Get current snapshot for selector.
	 *
	 * @param selector - Function to select data from state
	 * @returns Selected value
	 */
	getSnapshot<T>(selector: DataSelector<T>): T {
		return selector(this.state);
	}

	/**
	 * Get the full state snapshot.
	 * Used for debugging and testing.
	 *
	 * @returns Current state
	 */
	getState(): DataStoreState {
		return this.state;
	}

	/**
	 * Get transfer state by ID.
	 *
	 * When called with an explicit type parameter the returned `data` / `originalData`
	 * fields are narrowed to `StrictTransferData<T> | null`.
	 *
	 * @typeParam T - Optional strict transfer shape
	 * @param transferId - Transfer identifier
	 * @returns Transfer state (optionally narrowed) or undefined
	 */
	getTransfer<T extends Record<string, unknown> = Record<string, unknown>>(
		transferId: string
	): (TransferState & { data: StrictTransferData<T> | null; originalData: StrictTransferData<T> | null }) | undefined {
		return this.state.transfers.get(transferId) as
			| (TransferState & { data: StrictTransferData<T> | null; originalData: StrictTransferData<T> | null })
			| undefined;
	}

	/**
	 * Set transfer data.
	 * Resets dirty state and error.
	 *
	 * @typeParam T - Optional strict transfer shape
	 * @param transferId - Transfer identifier
	 * @param data - Transfer data (typed when T is provided)
	 */
	setTransferData<T extends Record<string, unknown> = Record<string, unknown>>(
		transferId: string,
		data: StrictTransferData<T>
	): void {
		this.updateState((transfers) => {
			const current = transfers.get(transferId) || createEmptyTransferState();
			transfers.set(transferId, {
				...current,
				data,
				originalData: data,
				isDirty: false,
				isLoading: false,
				error: null,
			});
		});
	}

	/**
	 * Update a single field on a transfer.
	 * If the transfer doesn't exist, creates it with just this field.
	 *
	 * When `T` is provided the `field` parameter is restricted to `keyof T`
	 * and `value` is narrowed to the matching property type.
	 *
	 * @typeParam T - Optional strict transfer shape
	 * @param transferId - Transfer identifier
	 * @param field - Field name (restricted to `keyof T` when strict)
	 * @param value - New value (typed to `T[K]` when strict)
	 */
	updateTransferField<
		T extends Record<string, unknown> = Record<string, unknown>,
		K extends string & keyof T = string & keyof T,
	>(transferId: string, field: K, value: T[K]): void {
		this.updateState((transfers) => {
			const current = transfers.get(transferId);
			if (current?.data) {
				// Transfer exists - update the field
				const newData = { ...current.data, [field]: value };
				const isDirty = !deepEqual(newData, current.originalData);
				transfers.set(transferId, {
					...current,
					data: newData,
					isDirty,
				});
			} else {
				// Transfer doesn't exist yet - create it with this field
				// This happens when typing into a form before data is loaded
				const newData = { [field]: value };
				transfers.set(transferId, {
					...(current || createEmptyTransferState()),
					data: newData,
					originalData: null, // No original yet - created locally
					isDirty: true, // New transfer is dirty by definition
					isLoading: false,
				});
			}
		});
	}

	/**
	 * Set transfer loading state.
	 *
	 * @param transferId - Transfer identifier
	 * @param isLoading - Loading state
	 */
	setTransferLoading(transferId: string, isLoading: boolean): void {
		this.updateState((transfers) => {
			const current = transfers.get(transferId) || createEmptyTransferState();
			transfers.set(transferId, {
				...current,
				isLoading,
			});
		});
	}

	/**
	 * Set transfer error state.
	 *
	 * @param transferId - Transfer identifier
	 * @param error - Error or null to clear
	 */
	setTransferError(transferId: string, error: Error | null): void {
		this.updateState((transfers) => {
			const current = transfers.get(transferId) || createEmptyTransferState();
			transfers.set(transferId, {
				...current,
				error,
				isLoading: false,
			});
		});
	}

	/**
	 * Reset transfer to original data.
	 *
	 * @param transferId - Transfer identifier
	 */
	resetTransfer(transferId: string): void {
		this.updateState((transfers) => {
			const current = transfers.get(transferId);
			if (current) {
				transfers.set(transferId, {
					...current,
					data: current.originalData,
					isDirty: false,
				});
			}
		});
	}

	/**
	 * Clear transfer from store.
	 *
	 * @param transferId - Transfer identifier
	 */
	clearTransfer(transferId: string): void {
		this.updateState((transfers) => {
			transfers.delete(transferId);
		});
	}

	/**
	 * Clear all transfers from store.
	 */
	clearAll(): void {
		this.updateState((transfers) => {
			transfers.clear();
		});
	}

	/**
	 * Update multiple fields at once.
	 *
	 * When `T` is provided the `updates` object is restricted to partial keys of `T`.
	 *
	 * @typeParam T - Optional strict transfer shape
	 * @param transferId - Transfer identifier
	 * @param updates - Object with field updates (typed to `Partial<T>` when strict)
	 */
	updateTransferFields<T extends Record<string, unknown> = Record<string, unknown>>(
		transferId: string,
		updates: Partial<T>
	): void {
		this.updateState((transfers) => {
			const current = transfers.get(transferId);
			if (current?.data) {
				const newData = { ...current.data, ...updates };
				const isDirty = !deepEqual(newData, current.originalData);
				transfers.set(transferId, {
					...current,
					data: newData,
					isDirty,
				});
			}
		});
	}

	/**
	 * Internal method to update state and notify subscribers.
	 */
	private updateState(updater: (transfers: Map<string, TransferState>) => void): void {
		const prevState = this.state;
		const newEntities = new Map(this.state.transfers);
		updater(newEntities);

		this.state = {
			transfers: newEntities,
			version: this.state.version + 1,
		};

		// Notify only affected subscribers
		if (this.state !== prevState) {
			this.notifySubscribers();
		}
	}

	/**
	 * Notify subscribers whose selected values have changed.
	 */
	private notifySubscribers(): void {
		for (const [selector, callbacks] of this.subscribers) {
			const prevValue = this.selectorCache.get(selector);
			const newValue = selector(this.state);

			// Only notify if value changed (reference equality)
			if (prevValue !== newValue) {
				this.selectorCache.set(selector, newValue);
				for (const cb of callbacks) {
					cb();
				}
			}
		}
	}
}
